/**
 * RiskRadarChart.jsx
 * 
 * Radar chart visualization for 18 health risk categories
 * Shows all risks in a spider/radar chart with color-coded levels
 */

import React, { useMemo } from 'react';

const RiskRadarChart = ({
    data = [], // Support the data prop passed from ResultsScreen
    risks = {},
    systemSummary = {},
    size = 300,
    showLabels = true,
    interactive = true
}) => {
    const center = size / 2;
    const maxRadius = (size / 2) - 45;

    // Use data from props or derive from systemSummary
    const chartData = useMemo(() => {
        if (data && data.length > 0) return data;

        // Fallback to 5 systems if data prop is not provided
        const systems = [
            { id: 'nervous', label: 'Thần kinh', icon: '🧠', color: '#8B5CF6' },
            { id: 'cardiovascular', label: 'Tim mạch', icon: '❤️', color: '#EF4444' },
            { id: 'digestive', label: 'Tiêu hóa', icon: '🍽️', color: '#F59E0B' },
            { id: 'functional', label: 'Chức năng', icon: '⚖️', color: '#10B981' },
            { id: 'metabolic', label: 'Chuyển hóa', icon: '🔋', color: '#3B82F6' }
        ];

        return systems.map(sys => ({
            category: sys.label,
            value: systemSummary[sys.id] || 0,
            color: sys.color,
            icon: sys.icon
        }));
    }, [data, systemSummary]);

    const numAxes = chartData.length;
    const angleStep = (2 * Math.PI) / numAxes;

    // Calculate point position
    const getPoint = (index, value) => {
        const angle = (index * angleStep) - (Math.PI / 2);
        const radius = (value / 100) * maxRadius;
        return {
            x: center + radius * Math.cos(angle),
            y: center + radius * Math.sin(angle)
        };
    };

    // Generate polygon points
    const polygonPoints = useMemo(() => {
        if (chartData.length === 0) return "";
        return chartData.map((d, i) => {
            const point = getPoint(i, d.value);
            return `${point.x},${point.y}`;
        }).join(' ');
    }, [chartData]);

    const gridLevels = [20, 40, 60, 80, 100];

    const getScoreColor = (score) => {
        if (score >= 80) return '#EF4444'; // Red
        if (score >= 60) return '#F59E0B'; // Orange
        if (score >= 40) return '#10B981'; // Green
        return '#34D399'; // Light Green
    };

    const gradientId = `riskGradient-${Math.random().toString(36).substr(2, 9)}`;

    return (
        <div className="risk-radar-chart relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <defs>
                    <radialGradient id={gradientId}>
                        <stop offset="0%" stopColor="rgba(59, 130, 246, 0.2)" />
                        <stop offset="100%" stopColor="rgba(59, 130, 246, 0.5)" />
                    </radialGradient>
                </defs>

                {/* Grid circles */}
                {gridLevels.map(level => (
                    <circle
                        key={level}
                        cx={center}
                        cy={center}
                        r={(level / 100) * maxRadius}
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="1"
                    />
                ))}

                {/* Axis lines */}
                {chartData.map((d, i) => {
                    const endPoint = getPoint(i, 100);
                    return (
                        <line
                            key={i}
                            x1={center}
                            y1={center}
                            x2={endPoint.x}
                            y2={endPoint.y}
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="1"
                        />
                    );
                })}

                {/* Risk polygon */}
                {polygonPoints && (
                    <polygon
                        points={polygonPoints}
                        fill={`url(#${gradientId})`}
                        stroke="rgba(59, 130, 246, 0.8)"
                        strokeWidth="2"
                        className="animate-fade-in"
                    />
                )}

                {/* Data points */}
                {chartData.map((d, i) => {
                    const point = getPoint(i, d.value);
                    return (
                        <circle
                            key={i}
                            cx={point.x}
                            cy={point.y}
                            r="4"
                            fill={getScoreColor(d.value)}
                            stroke="#fff"
                            strokeWidth="1.5"
                            className="shadow-sm"
                        />
                    );
                })}

                {/* Labels (Icons) */}
                {showLabels && chartData.map((d, i) => {
                    const labelPoint = getPoint(i, 115);
                    return (
                        <text
                            key={i}
                            x={labelPoint.x}
                            y={labelPoint.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="16"
                        >
                            {d.icon || '📍'}
                        </text>
                    );
                })}

                {/* Center score */}
                <g className="pointer-events-none">
                    <text
                        x={center}
                        y={center - 5}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="20"
                        fontWeight="bold"
                        fill="#fff"
                    >
                        {Math.round(chartData.reduce((a, b) => a + b.value, 0) / chartData.length) || 0}
                    </text>
                    <text
                        x={center}
                        y={center + 12}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="8"
                        fill="rgba(255,255,255,0.5)"
                        className="uppercase tracking-widest font-bold"
                    >
                        Rủi ro
                    </text>
                </g>
            </svg>
        </div>
    );
};

export default RiskRadarChart;
